import * as api from "./client.js";
import { type DownloadedFile, readDownload } from "./fileDownload.js";
import type { TaxonomyMode } from "./submissions.service.js";

export type PaginatedResponse<T> = {
  items: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
};

export type Testimonial = {
  id: string;
  externalId: string | null;
  processingItemId: string | null;
  sourceTestimonialId: string | null;
  editorialCategoryId: number | null;
  publicationId: number | null;
  practiceAreaId: number | null;
  lawyerName: string | null;
  content: string;
  language: string;
  firmRef: string;
  status: string;
  companyId: number | null;
  surveyResponseId: string | null;
  displayOrder: number | null;
  testimonialType: string | null;
  contactId: string | null;
  tableId: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  originalCreatedAt: string | null;
  publicationName: string | null;
  practiceAreaName: string | null;
  firmName: string | null;
  regionName: string | null;
  edition: string | null;
  tableName: string | null;
  editionId: string | null;
  rankingEntityId: string | null;
  bpaId: string | null;
  editionBpaId: string | null;
  rankingTableId: string | null;
  isTranslation: boolean;
  relatedTestimonials: { id: string; language: string }[] | null;
  // Sentiment classification: "positive" | "negative" | "neutral", or null when not classified
  // (e.g. CSV-imported rows).
  sentiment: string | null;
};

export type UpdateTestimonialResponse = {
  testimonial: Testimonial;
  /**
   * Present only when rejecting a source testimonial also took down its
   * translations and copies; lists every row set to REJECTED, including the
   * one that was updated.
   */
  takedown?: {
    testimonials: { id: string; language: string }[];
  };
  suspended?: {
    testimonials: { id: string; language: string }[];
  };
};

export type TestimonialDecisionStatus = "APPROVED" | "REJECTED" | "SUSPENDED";

export type UnlinkTestimonialType = "set_testimonial" | "clerk_testimonial";

export type UnlinkLawyerResponse = {
  processingItemIds: string[];
  testimonialIds: string[];
  approvalRequestsUpdated: number;
  auditEmitFailed: boolean;
};

export type ProcessingItem = {
  id: string;
  externalId: string | null;
  parentProcessingItemId: string | null;
  langInput: string | null;
  langOutput: string | null;
  status: string | null;
  transformations: string | null;
  retryCount: number;
  lastError: string | null;
  lastErrorAt: string | null;
  updatedAt: string | null;
  createdAt: string | null;
};

export type ProcessingItemDetail = ProcessingItem & {
  contentInput: string | null;
  contentTransformed: string | null;
  contentOutput: string | null;
  metadata: unknown;
};

export type ProcessingItemsResponse = PaginatedResponse<ProcessingItem> & {
  statuses: string[];
};

export type TestimonialsQueryParams = {
  page?: number;
  pageSize?: number;
  id?: string;
  externalId?: string;
  status?: string;
  language?: string;
  firmRef?: string;
  search?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  publicationId?: number;
  practiceAreaId?: number;
  editorialCategoryId?: number;
  companyId?: number;
  lawyerName?: string;
  // Filters on content_processor_pipeline.testimonial_type (backend reads "testimonialType")
  testimonialType?: string;
  // Plain region name string; backend filters WHERE region_name = regionName
  regionName?: string;
  // Filters by sentiment classification: "positive" | "negative" | "neutral" (lowercase)
  sentiment?: string;
  taxonomy?: TaxonomyMode;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
};

export type TestimonialFilterParams = Pick<
  TestimonialsQueryParams,
  | "taxonomy"
  | "publicationId"
  | "editionId"
  | "practiceAreaId"
  | "bpaId"
  | "regionName"
  | "rankingEntityId"
  | "companyId"
  | "lawyerName"
  | "testimonialType"
  | "language"
  | "sentiment"
  | "search"
>;

type TestimonialExportScope =
  | { taxonomy?: "legacy"; publicationId: number }
  | { taxonomy: "new"; editionId: string };

export type TestimonialExportRequest = Omit<
  TestimonialFilterParams,
  "taxonomy" | "companyId" | "publicationId" | "editionId" | "testimonialType"
> &
  TestimonialExportScope & {
    type?: string;
    companyId: number;
    visibleOnly: boolean;
    firmName?: string;
    publicationName?: string;
    edition?: string;
  };

export const TESTIMONIAL_EXPORT_FALLBACK_FILENAME = "approved-testimonials.xlsx";

export type TestimonialUniques = {
  publication: { editionId: string; publicationName: string; edition: string | null };
  region: { rankingEntityId: string; regionName: string };
  practice: { bpaId: string; practiceAreaName: string };
};

export type TestimonialUniqueField = keyof TestimonialUniques;

export type TestimonialUniquesFilters = { editionId?: string; rankingEntityId?: string };

export type ProcessingItemsQueryParams = {
  page?: number;
  pageSize?: number;
  id?: string;
  externalId?: string;
  status?: string;
  langInput?: string;
  langOutput?: string;
  search?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
};

export type RawTestimonial = {
  id: string;
  code: string | null;
  editorialCategoryId: number | null;
  contentType: string | null;
  content: string | null;
  lawyerName: string | null;
  publication: string | null;
  edition: string | null;
  publicationId: number;
  firmName: string | null;
  firmRef: number | null;
  companyId: number | null;
  editorialPostId: number | null;
  contactId: string | null;
  tableId: number | null;
  updatedAt: string;
};

export type RawTestimonialsQueryParams = {
  page?: number;
  pageSize?: number;
  id?: string;
  publication?: string;
  edition?: string;
  firmRef?: string;
  search?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
};

export type SyncStateResponse = {
  exists: boolean;
  syncState: {
    id: string;
    lastSyncAt: string;
    lastDataDt: string | null;
    updatedAt: string;
  } | null;
};

export type FixesStats = {
  missingCompanyId: number;
};

export type UpdateCompanyIdResponse = {
  message: string;
  updatedTestimonialsCount: number;
  updatedProcessingItemsCount: number;
};

export type SyncStateUpdateResponse = {
  message: string;
  previousLastDataDt: string | null;
  newLastDataDt: string | null;
};

export type CsvImportResult = {
  inserted: number;
  total: number;
};

export type RequeueResponse = {
  requeued: number;
  targetQueue: string;
  message: string;
};

export type TranslateFrStatsParams = {
  publicationId: number;
  regionName: string;
  sourceLanguage: string;
  targetLanguage: string;
};

export type TranslateFrStats = {
  total: number;
  translated: number;
  remaining: number;
};

export type TranslateFrBatchParams = TranslateFrStatsParams & {
  batchSize: number;
};

export type TranslateFrFailure = {
  externalId: string;
  error: string;
};

export type TranslateFrBatchResponse = {
  processed: number;
  skipped: number;
  failed: number;
  remaining: number;
  failures: TranslateFrFailure[];
};

export type FranceToParisStats = {
  total: number;
  copied: number;
  remaining: number;
  unmapped: number;
};

export type FranceToParisBatchParams = {
  batchSize: number;
};

export type FranceToParisBatchResponse = {
  inserted: number;
  remaining: number;
};

export type NewAxisField =
  | "editionId"
  | "rankingEntityId"
  | "bpaId"
  | "editionBpaId"
  | "rankingTableId";

export type MissingNewAxisIdsItem = {
  processingItemId: string;
  externalId: string | null;
  publicationId: number | null;
  publicationName: string | null;
  practiceAreaId: number | null;
  practiceAreaName: string | null;
  status: string | null;
  createdAt: string | null;
  missing: NewAxisField[];
  repairable: boolean;
  unresolved: {
    field: NewAxisField;
    reason: "missing" | "unmapped" | "ambiguous" | "inconsistent";
  }[];
};

export type MissingNewAxisIdsScan = {
  total: number;
  repairable: number;
  outsideBridge: number;
  listed: number;
  items: MissingNewAxisIdsItem[];
};

export type MissingNewAxisIdsApplyResponse = {
  updated: number;
  processingItems: number;
  testimonials: number;
  remaining: number;
};

// --- France → Paris (user-chosen practice mappings) ---

export type FranceToParisMappedMapping = {
  sourceEditorialCategoryId: number;
  targetEditorialCategoryId: number;
  targetPracticeAreaId: number | null;
  targetPracticeAreaName: string | null;
  targetTableId: number | null;
  targetTableName: string | null;
};

// Shared request body for both stats and batch endpoints (batch adds batchSize).
export type FranceToParisMappedFilters = {
  sourcePublicationId: number;
  targetPublicationId: number;
  targetPublicationName: string;
  targetRegionName: string;
  language: string;
  mappings: FranceToParisMappedMapping[];
};

export type FranceToParisMappedStats = {
  total: number;
  copied: number;
  remaining: number;
};

export type FranceToParisMappedBatchParams = FranceToParisMappedFilters & {
  batchSize: number;
};

export type FranceToParisMappedBatchResponse = {
  inserted: number;
  remaining: number;
};

export type SyncRunStat = {
  id: string;
  syncStateId: string;
  startedAt: string;
  completedAt: string;
  processed: number;
  created: number;
  skipped: number;
  batches: number;
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      query.append(key, String(value));
    }
  }
  const str = query.toString();
  return str ? `?${str}` : "";
}

export const pipelineService = {
  async getTestimonials(
    params: TestimonialsQueryParams,
    signal?: AbortSignal,
  ): Promise<PaginatedResponse<Testimonial>> {
    const endpoint = `/v1/content-processor/staff/testimonials${buildQuery(params)}`;
    return signal ? api.getWithSignal(endpoint, signal) : api.get(endpoint);
  },

  async exportTestimonials(request: TestimonialExportRequest): Promise<DownloadedFile> {
    const response = await api.fetchRaw("/v1/content-processor/staff/testimonials/export", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
    return readDownload(response, TESTIMONIAL_EXPORT_FALLBACK_FILENAME);
  },

  async getTestimonialLanguages(): Promise<{ languages: string[] }> {
    return api.get("/v1/content-processor/staff/testimonials/languages");
  },

  async getTestimonialUniques<Field extends TestimonialUniqueField>(
    field: Field,
    filters: TestimonialUniquesFilters = {},
  ): Promise<{ values: TestimonialUniques[Field][] }> {
    const query = buildQuery({ field, taxonomy: "new", ...filters });
    return api.get(`/v1/content-processor/staff/testimonials/uniques${query}`);
  },

  async getProcessingItems(params: ProcessingItemsQueryParams): Promise<ProcessingItemsResponse> {
    const query = buildQuery(params);
    return api.get(`/v1/content-processor/developer/processing-items${query}`);
  },

  async getProcessingItemDetail(id: string): Promise<ProcessingItemDetail> {
    return api.get(`/v1/content-processor/developer/processing-items/${id}`);
  },

  async getRawTestimonials(
    params: RawTestimonialsQueryParams,
  ): Promise<PaginatedResponse<RawTestimonial>> {
    const query = buildQuery(params);
    return api.get(`/v1/content-processor/developer/raw-testimonials${query}`);
  },

  async getSyncState(): Promise<SyncStateResponse> {
    return api.get("/v1/content-processor/developer/sync-state");
  },

  async updateSyncState(lastDataDt: string | null): Promise<SyncStateUpdateResponse> {
    return api.patch("/v1/content-processor/developer/sync-state", { lastDataDt });
  },

  async getFixesStats(): Promise<FixesStats> {
    return api.get("/v1/content-processor/developer/fixes/stats");
  },

  async updateMissingCompanyId(): Promise<UpdateCompanyIdResponse> {
    return api.post("/v1/content-processor/developer/fixes/update-company-id", {});
  },

  async getTranslateFrStats(params: TranslateFrStatsParams): Promise<TranslateFrStats> {
    const query = buildQuery(params);
    return api.get(`/v1/content-processor/developer/fixes/translate-fr/stats${query}`);
  },

  async translateFrBatch(params: TranslateFrBatchParams): Promise<TranslateFrBatchResponse> {
    return api.post("/v1/content-processor/developer/fixes/translate-fr", params);
  },

  async getFranceToParisStats(): Promise<FranceToParisStats> {
    return api.get("/v1/content-processor/developer/fixes/france-to-paris/stats");
  },

  async franceToParisBatch(params: FranceToParisBatchParams): Promise<FranceToParisBatchResponse> {
    return api.post("/v1/content-processor/developer/fixes/france-to-paris", params);
  },

  async getMissingNewAxisIdsStats(): Promise<MissingNewAxisIdsScan> {
    return api.get("/v1/content-processor/developer/fixes/missing-new-axis-ids/stats");
  },

  async missingNewAxisIdsBatch(params: {
    batchSize: number;
  }): Promise<MissingNewAxisIdsApplyResponse> {
    return api.post("/v1/content-processor/developer/fixes/missing-new-axis-ids", params);
  },

  async getFranceToParisMappedStats(
    body: FranceToParisMappedFilters,
  ): Promise<FranceToParisMappedStats> {
    return api.post("/v1/content-processor/developer/fixes/france-to-paris-mapped/stats", body);
  },

  async franceToParisMappedBatch(
    body: FranceToParisMappedBatchParams,
  ): Promise<FranceToParisMappedBatchResponse> {
    return api.post("/v1/content-processor/developer/fixes/france-to-paris-mapped", body);
  },

  async getSyncRunStats(limit = 20): Promise<{ runs: SyncRunStat[] }> {
    return api.get(`/v1/content-processor/developer/sync-run-stats?limit=${limit}`);
  },

  async importRawTestimonials(records: RawTestimonial[]): Promise<CsvImportResult> {
    return api.post("/v1/content-processor/developer/raw-testimonials/import", { records });
  },

  async requeueProcessingItems(status: string, limit?: number): Promise<RequeueResponse> {
    return api.post("/v1/content-processor/developer/processing-items/requeue", {
      status,
      limit: limit ?? 100000,
    });
  },

  async updateTestimonial(
    id: string,
    data: {
      content?: string;
      status?: string;
      lawyerName?: string | null;
      language?: string;
      displayOrder?: number | null;
    },
  ): Promise<UpdateTestimonialResponse> {
    return api.patch(`/v1/content-processor/staff/testimonials/${id}`, data);
  },

  async updateContactId(id: string, contactId: string): Promise<{ testimonial: Testimonial }> {
    return api.patch(`/v1/content-processor/staff/testimonials/${id}/contact-id`, { contactId });
  },

  async unlinkLawyerFromProcessingItem(
    processingItemId: string,
    testimonialType?: UnlinkTestimonialType,
  ): Promise<UnlinkLawyerResponse> {
    return api.post(
      `/v1/content-processor/staff/processing-items/${processingItemId}/unlink-lawyer`,
      testimonialType ? { testimonialType } : {},
    );
  },

  async unlinkLawyerFromTestimonial(
    testimonialId: string,
    testimonialType?: UnlinkTestimonialType,
  ): Promise<UnlinkLawyerResponse> {
    return api.post(
      `/v1/content-processor/staff/testimonials/${testimonialId}/unlink-lawyer`,
      testimonialType ? { testimonialType } : {},
    );
  },
};
